import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { formatQty } from '../lib/format.js';

const { t } = i18n;

interface ContentLine {
  skuId: string;
  remainingQty: string;
  sku: { code: string; nameLt: string };
}

export function MoveToShelfDialog({
  palletId,
  contents,
  onClose,
  onMoved,
}: {
  palletId: string;
  contents: ContentLine[];
  onClose: () => void;
  onMoved: () => void;
}) {
  const available = contents.filter((line) => Number(line.remainingQty) > 0);
  const [selected, setSelected] = useState<Record<string, number>>(() =>
    Object.fromEntries(available.map((line) => [line.skuId, Number(line.remainingQty)])),
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lines = available
    .filter((line) => selected[line.skuId] !== undefined)
    .map((line) => ({ skuId: line.skuId, qty: selected[line.skuId]! }));
  const valid =
    reason.trim().length > 0 &&
    lines.length > 0 &&
    lines.every(
      (line) =>
        line.qty > 0 &&
        line.qty <= Number(available.find((row) => row.skuId === line.skuId)!.remainingQty),
    );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/pallets/${palletId}/move-to-shelf`, {
        method: 'POST',
        body: { lines, reason: reason.trim() },
      });
      onMoved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'insufficient_pallet_content'
          ? t('pallet.shelf.insufficient')
          : t('common.error'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('pallet.shelf.title')} onClose={onClose}>
      <form onSubmit={(event) => void submit(event)} className="space-y-3 text-sm">
        <p className="text-muted-foreground">{t('pallet.shelf.hint')}</p>
        <div className="space-y-2">
          {available.map((line) => {
            const checked = selected[line.skuId] !== undefined;
            return (
              <div
                key={line.skuId}
                className="flex flex-wrap items-center gap-2 rounded border border-border p-2"
              >
                <label className="flex min-w-0 flex-1 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) =>
                      setSelected((prev) => {
                        const next = { ...prev };
                        if (event.target.checked) next[line.skuId] = Number(line.remainingQty);
                        else delete next[line.skuId];
                        return next;
                      })
                    }
                  />
                  <span>
                    <span className="font-mono">{line.sku.code}</span> — {line.sku.nameLt} (
                    {formatQty(Number(line.remainingQty))})
                  </span>
                </label>
                {checked ? (
                  <NumberInput
                    integer
                    min={1}
                    max={Number(line.remainingQty)}
                    value={selected[line.skuId]!}
                    onChange={(qty) => setSelected((prev) => ({ ...prev, [line.skuId]: qty ?? 0 }))}
                    aria-label={`${t('pallet.shelf.qty')}: ${line.sku.code}`}
                    className="w-24 rounded border border-border bg-background px-2 py-1 tabular-nums"
                  />
                ) : null}
              </div>
            );
          })}
        </div>
        <label className="block space-y-1">
          <span>{t('pallet.shelf.reason')}</span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            maxLength={2000}
            className="w-full rounded border border-border bg-background px-2 py-1"
          />
        </label>
        {error ? (
          <p role="alert" className="text-red-600">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={!valid || busy}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
          >
            {t('pallet.shelf.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
